import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import cx from 'classnames'

import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Button, Menu, MenuItem, Typography } from '@material-ui/core'

import { ShoppingCart } from '@material-ui/icons'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import DrawerEcommerce from '~/components/Ecommerce/DrawerEcommerce'
import useViewportSize from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'

import track from '~/track'
import api from '~/api'
import { mountUrlGoogleShopping } from '~/utils'

import googleShoppingIcon from '~/assets/googleShopping.svg'
import rappiIcon from '~/assets/rappi-logo.svg'

const useStyles = makeStyles((theme) => ({
  button: {
    boxShadow: 'none'
  },
  buttonRappi: {
    background: theme.palette.grey[800],
    color: '#FFF',
    '&:hover': {
      background: theme.palette.grey[900]
    },
    '& img': {
      width: 45,
      [theme.breakpoints.up('sm')]: {
        marginLeft: 8
      }
    },
    boxShadow: 'none'
  },
  buttonSecondary: {
    color: '#121212',
    backgroundColor: '#EEEBEB',
    border: '2px solid #121212',
    '&:hover': {
      backgroundColor: '#CCC'
    }
  },
  paper: {
    border: '2px solid #EEEBEB'
  },
  icon: {
    width: 20,
    marginLeft: 8
  }
}))

function trackApi(data, product, store, config, position) {
  api.clickTrack({
    gtin: product && product.gtin,
    productName: product && product.name,
    name: store ? store.name : 'all',
    cnpj: store ? store.cnpj : '00000000000000',
    brand: config.isOndetem ? product.brand : null,
    user: position?.user,
    ...data
  })
}

function handleClickRappi(brandName, handleClose, product, store, config, position) {
  let url

  if (!product) {
    url = `https://www.rappi.com.br/search?store_type=all&query=${brandName}&search_type=TYPED`
    track('rappi', { category: 'menu', name: brandName })
  } else {
    try {
      url = `https://www.rappi.com.br/search?store_type=all&query=${product?.name}&search_type=TYPED`
      track('rappi', { category: 'produto', name: product?.name })

      trackApi({ event: 'buyWithRappi', url, brandName }, product, store, config, position)
    } catch (err) {
      console.log('Rappi error: ', err)
    }
  }

  handleClose()
  window.open(url)
}

function ButtonBuyOnline({
  store,
  modal,
  color,
  config,
  product,
  disabled,
  position,
  secondary,
  dataEcommerce,
  openEcommerce,
  loadingEcommerce,
  setApiEcommerce,
  setOpenEcommerce
}) {
  const classes = useStyles({ color })
  const theme = useTheme()
  const viewport = useViewportSize()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const [anchorEl, setAnchorEl] = useState(null)

  const { brandName, fixedLink, buyWithRappi, citysAllowedRappi, showBuyOnline, showKnowMore } =
    config
  let { buyOnline } = config
  const { getLiteral } = useLiterals()
  const isGoogleShopping = buyOnline === 'googleShopping'

  const handleClick = (event) => setAnchorEl(event.currentTarget)

  const handleClose = () => setAnchorEl(null)

  const handleClickButton = useCallback(() => {
    const url = isGoogleShopping
      ? mountUrlGoogleShopping(brandName, product?.gtin, getLiteral)
      : product?.url || fixedLink

    const event = isGoogleShopping ? 'comprarOnline-GoogleShopping' : 'comprarOnline-URL'

    track(event, { category: modal ? 'produto' : 'lojas', name: product?.name })
    trackApi({ event: 'buyOnline', url, buyOnline }, product, store, config, position)

    window.open(url)
  }, [brandName, buyOnline, config, fixedLink, modal, product, store])

  const handleClickEcommerce = () => {
    setApiEcommerce({ name: 'intellibrand' })
    trackApi({ event: 'ecommerce', brandName }, product, store, config, position)

    if (secondary) {
      trackApi({ event: 'postRupture', brandName }, product, store, config, position)
      track('naoLocalizadoProximo-CliqueRedirect', {
        category: 'menu',
        name: product ? product.name : brandName
      })
    }

    track('comprarOnline', {
      category: modal ? 'produto' : 'lojas',
      name: product?.name
    })

    if (product) return setOpenEcommerce(product)
  }

  const handleClickEcommerceGoogle = () => {
    setApiEcommerce({ name: 'ecommerceGoogle', location: position?.user?.city })
    trackApi({ event: 'ecommerce', brandName }, product, store, config, position)

    if (secondary) {
      trackApi({ event: 'postRupture', brandName }, product, store, config, position)
      track('naoLocalizadoProximo-CliqueRedirect', {
        category: 'menu',
        name: product ? product.name : brandName
      })
    }

    const event = isGoogleShopping ? 'comprarOnline-GoogleShopping' : 'comprarOnline'
    track(event, {
      category: modal ? 'produto' : 'lojas',
      name: product?.name
    })

    if (product) return setOpenEcommerce(product)

    setOpenEcommerce('all')
  }

  const checkLink = () => {
    if (product && product.url) return product.url
    if (fixedLink) return fixedLink

    return null
  }

  const showRappiButton =
    buyWithRappi &&
    (citysAllowedRappi || [])
      .filter((city) => Boolean(city))
      .map((city) => city.split('-')[0].trim().toLowerCase())
      .indexOf(position?.user?.city?.toLowerCase()) !== -1

  const hasShowBuyOnlineChecked = Boolean(showBuyOnline)
  const hasShowKnowMoreChecked = Boolean(showKnowMore)

  if (!hasShowBuyOnlineChecked) buyOnline = 'off'

  if (buyOnline === 'knowMore' || (hasShowKnowMoreChecked && !hasShowBuyOnlineChecked)) {
    return (
      <Button
        component="a"
        color="primary"
        variant="contained"
        href={product?.url || config.knowMore}
        target="_blank"
        rel="noopener noreferrer"
        data-cy="button-knowMore"
        startIcon={<InfoOutlinedIcon />}
        className={classes.button}
      >
        {getLiteral('1345')}
      </Button>
    )
  }

  if (buyOnline === 'ecommerce' || buyOnline === 'ecommerceGoogle') {
    return (
      <>
        <DrawerEcommerce
          openEcommerce={openEcommerce}
          setOpenEcommerce={setOpenEcommerce}
          dataEcommerce={dataEcommerce}
          loadingEcommerce={loadingEcommerce}
          setApiEcommerce={setApiEcommerce}
          config={config}
        />

        <Button
          color="primary"
          variant="contained"
          data-cy="button-ecommerce"
          className={cx(classes.button, { [classes.buttonSecondary]: secondary })}
          onClick={buyOnline === 'ecommerce' ? handleClickEcommerce : handleClickEcommerceGoogle}
          disabled={disabled}
          startIcon={<ShoppingCart />}
        >
          {getLiteral('1317')}
        </Button>
      </>
    )
  }

  if (
    (buyOnline !== 'off' && !showRappiButton && checkLink()) ||
    (buyOnline === 'googleShopping' && !showRappiButton)
  ) {
    return (
      <Button
        color="primary"
        variant="contained"
        data-cy="button-buyOnline"
        disabled={disabled}
        onClick={handleClickButton}
        className={!secondary ? '' : classes.buttonSecondary}
        startIcon={<ShoppingCart />}
      >
        {getLiteral('1317')}
      </Button>
    )
  }

  if (
    (showRappiButton && buyOnline === 'off') ||
    (showRappiButton && buyOnline === 'fixedLink' && !checkLink())
  ) {
    return (
      <Button
        component="a"
        variant="contained"
        data-cy="button-buyWithRappi"
        disabled={disabled}
        className={classes.buttonRappi}
        onClick={() => handleClickRappi(brandName, handleClose, product, store, config)}
        startIcon={<ShoppingCart />}
      >
        {getLiteral('1317')} <img src={rappiIcon} alt="imagem-rappi" />
      </Button>
    )
  }

  if (
    (showRappiButton && buyOnline !== 'off' && checkLink()) ||
    (showRappiButton && buyOnline === 'googleShopping')
  ) {
    return (
      <>
        <Button
          aria-haspopup="true"
          variant="contained"
          color="primary"
          data-cy="button-openMenu"
          className={!secondary ? '' : classes.buttonSecondary}
          onClick={handleClick}
          endIcon={<ExpandMoreIcon />}
        >
          {getLiteral('1317')}
        </Button>

        <Menu
          data-cy="menu-buyOnline"
          classes={{ paper: classes.paper }}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          elevation={0}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'left' }}
        >
          <MenuItem onClick={handleClickButton} data-cy="button-item-buyOnline">
            {buyOnline === 'fixedLink' ? (
              <>
                <Typography>{getLiteral('2039')}</Typography>
                <ShoppingCart className={classes.icon} />
              </>
            ) : (
              <>
                <Typography>{getLiteral('2040')} Shopping</Typography>
                <img className={classes.icon} src={googleShoppingIcon} alt="imagem-google" />
              </>
            )}
          </MenuItem>
          <MenuItem
            data-cy="button-item-buyWithRappi"
            className={classes.buttonRappi}
            onClick={() => handleClickRappi(brandName, handleClose, product, store, config)}
          >
            <>
              <Typography>{getLiteral('1317')}</Typography>
              <img className={classes.icon} src={rappiIcon} alt="imagem-rappi" />
            </>
          </MenuItem>
        </Menu>
      </>
    )
  }

  return null
}

export default compose(
  connect((state) => ({
    config: state.config
  }))
)(ButtonBuyOnline)
